import React, { useRef, useState } from 'react';
import { Store } from '../types';
import { ShoppingBag, ChevronRight, MapPin, Clock, Star, ShieldCheck, Zap, ArrowDown, Send, CheckCircle2 } from 'lucide-react';

interface LandingPageProps {
    stores: Store[];
}

const LandingPage: React.FC<LandingPageProps> = ({ stores }) => {
    const requestSectionRef = useRef<HTMLDivElement>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const scrollToRequest = () => {
        requestSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        // Simulate API call
        setTimeout(() => {
            setIsSubmitting(false);
            setIsSubmitted(true);
        }, 1500);
    };

    return (
        <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-emerald-100">
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-emerald-600 to-teal-700 py-20 px-6 text-white min-h-[80vh] flex flex-col items-center justify-center">
                <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                    <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M0 100 C 20 0 50 0 100 100 Z" fill="currentColor" />
                    </svg>
                </div>

                <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full border border-white/30 text-sm font-semibold mb-6 animate-fade-in-down">
                        <Star size={16} className="text-yellow-300 fill-yellow-300" />
                        우리 동네 1등 장보기 서비스
                    </div>

                    <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-tight">
                        신선함을 문 앞까지,<br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-emerald-200">
                            장봐드림
                        </span>이 찾아갑니다
                    </h1>

                    <p className="text-lg md:text-xl text-emerald-50/90 max-w-2xl mx-auto font-medium leading-relaxed">
                        아직 우리 지역에 장봐드림이 없나요?<br />
                        지금 바로 오픈 요청을 남겨주시면 가장 먼저 달려갈게요.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
                        <button
                            onClick={scrollToRequest}
                            className="w-full sm:w-auto px-8 py-4 bg-white text-emerald-700 font-bold rounded-2xl shadow-xl shadow-emerald-900/20 hover:bg-emerald-50 active:scale-95 transition-all flex items-center justify-center gap-2 group"
                        >
                            우리 지역 오픈 요청하기
                            <ArrowDown size={20} className="group-hover:translate-y-1 transition-transform" />
                        </button>
                    </div>
                </div>

                <div className="absolute bottom-10 animate-bounce cursor-pointer opacity-50" onClick={scrollToRequest}>
                    <ArrowDown size={24} />
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 px-6 max-w-6xl mx-auto">
                <div className="text-center mb-16 space-y-4">
                    <h2 className="text-3xl md:text-4xl font-bold">왜 장봐드림인가요?</h2>
                    <p className="text-gray-500 font-medium">우리는 단순히 배달을 넘어 상생과 신뢰를 연결합니다.</p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {[
                        {
                            icon: <Zap className="text-emerald-500" size={32} />,
                            title: "공동구매 특가",
                            desc: "이웃과 함께 모여 사니까 더 저렴하게. 오직 우리 동네 지점에서만 만나는 특별한 가격!"
                        },
                        {
                            icon: <ShieldCheck className="text-teal-500" size={32} />,
                            title: "엄선된 퀄리티",
                            desc: "매일 아침 가락시장에서 직접 선별한 신선한 과일과 채소만을 취급하여 믿을 수 있습니다."
                        },
                        {
                            icon: <MapPin className="text-emerald-600" size={32} />,
                            title: "우리 동네 픽업",
                            desc: "집 근처 단골 가게에서 편하게 픽업하세요. 배송비 부담 없이 퇴근길에 쏙!"
                        },
                    ].map((feature, idx) => (
                        <div key={idx} className="p-8 bg-gray-50 rounded-3xl border border-gray-100 hover:shadow-lg transition-shadow group">
                            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-6 group-hover:scale-110 transition-transform">
                                {feature.icon}
                            </div>
                            <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                            <p className="text-gray-600 leading-relaxed text-sm">{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Request Section */}
            <section ref={requestSectionRef} className="py-24 px-6 bg-emerald-50/50 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/20 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2"></div>

                <div className="max-w-xl mx-auto relative z-10">
                    <div className="bg-white rounded-[40px] shadow-2xl shadow-emerald-900/5 p-8 md:p-12 border border-white">
                        {isSubmitted ? (
                            <div className="text-center space-y-6 py-8 animate-in fade-in zoom-in duration-500">
                                <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-3xl mx-auto flex items-center justify-center">
                                    <CheckCircle2 size={40} />
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-2xl font-black">요청이 완료되었습니다!</h3>
                                    <p className="text-gray-500 font-medium">우리 지역에 장봐드림이 오픈하면<br />가장 먼저 연락드릴게요.</p>
                                </div>
                                <button
                                    onClick={() => setIsSubmitted(false)}
                                    className="text-emerald-600 font-bold text-sm hover:underline"
                                >
                                    다시 작성하기
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="space-y-2 text-center">
                                    <h2 className="text-3xl font-black tracking-tight">우리지역 오픈 요청</h2>
                                    <p className="text-gray-500 font-medium text-sm">희망하시는 지역을 알려주시면 적극 검토하겠습니다.</p>
                                </div>

                                <div className="space-y-5">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-wider">희망 지역</label>
                                        <input
                                            required
                                            placeholder="예: 서울시 강남구 역삼동"
                                            className="w-full px-5 py-4 bg-gray-50 border-transparent focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 rounded-2xl outline-none transition-all placeholder:text-gray-300 font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-wider">이름</label>
                                        <input
                                            required
                                            placeholder="성함을 입력해주세요"
                                            className="w-full px-5 py-4 bg-gray-50 border-transparent focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 rounded-2xl outline-none transition-all placeholder:text-gray-300 font-medium"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-wider">연락처</label>
                                        <input
                                            required
                                            type="tel"
                                            placeholder="010-0000-0000"
                                            className="w-full px-5 py-4 bg-gray-50 border-transparent focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 rounded-2xl outline-none transition-all placeholder:text-gray-300 font-medium"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full py-5 bg-gray-900 hover:bg-black text-white font-bold rounded-2xl shadow-xl shadow-gray-200 active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                                >
                                    {isSubmitting ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            요청 보내기
                                            <Send size={18} />
                                        </>
                                    )}
                                </button>

                                <p className="text-[10px] text-center text-gray-400 leading-relaxed font-medium">
                                    오픈 요청 시 개인정보 수집 및 이용에 동의하는 것으로 간주하며, 서비스 관련 안내 문자가 발송될 수 있습니다.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* How it Works Section */}
            <section className="bg-gray-900 py-24 px-6 text-white overflow-hidden">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <div className="space-y-8">
                        <div className="space-y-4">
                            <span className="text-emerald-400 font-bold uppercase tracking-widest text-sm">Service Flow</span>
                            <h2 className="text-3xl md:text-5xl font-black leading-tight">이용 방법은 <br />정말 간단합니다</h2>
                        </div>

                        <div className="space-y-6">
                            {[
                                { step: "01", title: "지역 오픈 요청", content: "우리 지역에 장봐드림이 들어오도록 요청하세요." },
                                { step: "02", title: "스마트한 주문", content: "지점 오픈 후 실시간으로 올라오는 공동구매에 참여하세요." },
                                { step: "03", title: "간편한 결제", content: "카카오톡으로 신청하고 현장에서 확인하세요." },
                                { step: "04", title: "퇴근길 픽업", content: "지정된 거점에서 상품을 수령하면 끝!" },
                            ].map((s, idx) => (
                                <div key={idx} className="flex gap-4">
                                    <div className="text-emerald-500 font-mono text-xl font-bold shrink-0">{s.step}</div>
                                    <div className="space-y-1">
                                        <h4 className="text-lg font-bold">{s.title}</h4>
                                        <p className="text-gray-400 text-sm leading-relaxed">{s.content}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative flex justify-center">
                        <div className="w-64 h-[500px] bg-white rounded-[40px] border-[12px] border-gray-800 shadow-2xl overflow-hidden relative">
                            <div className="bg-emerald-500 h-24 p-4 flex flex-col justify-end">
                                <div className="w-8 h-8 bg-white/20 rounded-lg mb-2"></div>
                                <div className="w-24 h-2 bg-white/50 rounded-full"></div>
                            </div>
                            <div className="p-4 space-y-4">
                                <div className="w-full h-32 bg-gray-100 rounded-2xl animate-pulse"></div>
                                <div className="space-y-2">
                                    <div className="w-2/3 h-4 bg-gray-200 rounded-full"></div>
                                    <div className="w-1/2 h-3 bg-gray-100 rounded-full"></div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="h-20 bg-gray-50 rounded-xl"></div>
                                    <div className="h-20 bg-gray-50 rounded-xl"></div>
                                </div>
                            </div>
                        </div>
                        <div className="absolute -top-10 -right-10 w-48 h-48 bg-emerald-500/20 blur-3xl rounded-full"></div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 border-t border-gray-100 text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center text-white">
                        <ShoppingBag size={16} />
                    </div>
                    <span className="font-black text-xl">장봐드림</span>
                </div>
                <p className="text-gray-400 text-sm">© 2024 장봐드림. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default LandingPage;
